import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, isAdmin?: boolean) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check for existing token and user data on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('currentUser');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const tokenData = JSON.parse(atob(storedToken));
        // Check if token is expired (7 days)
        const now = new Date().getTime();
        const tokenAge = now - tokenData.timestamp;
        const tokenMaxAge = 7 * 24 * 60 * 60 * 1000; // 7 days

        if (tokenAge > tokenMaxAge) {
          throw new Error('Token expired');
        }

        setUser(userData);
        setToken(storedToken);
        setIsAuthenticated(true);
        setIsAdmin(userData.role === 'admin');
      } catch (error) {
        console.error('Error parsing token:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
      }
    }
  }, []);

  const login = async (newToken: string, isAdmin: boolean = false) => {
    try {
      const tokenData = JSON.parse(atob(newToken));
      // Remove timestamp from user data
      const { timestamp, ...userData } = tokenData;
      setUser(userData);
      setToken(newToken);
      setIsAuthenticated(true);
      setIsAdmin(isAdmin || userData.role === 'admin');
      localStorage.setItem('token', newToken);
    } catch (error) {
      console.error('Error parsing token:', error);
      throw new Error('Invalid token');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
